import { ToastUtility } from "@syncfusion/ej2-react-notifications";
import {
  getOption,
  getTemplateParameters,
  printTemplate,
  printTemplateAsPdf,
} from "../services/ApiService";
import { LeakTestCertificate } from "../types/LeakTestCertificate";
import { PrintRequest } from "../types/PrintRequest";
import { TestType } from "../types/TestType";
import { AuthContext } from "./useAuth";
import { getPathItem, replaceExtension } from "./pathHelper";

export function isSkipped(certificate: LeakTestCertificate): boolean {
  return certificate.passed === undefined || certificate.passed === null;
}

export const SKIPPED = "Skipped";
export const PASSED = "Passed";
export const FAILED = "Failed";

const getTestResult = (certificate: LeakTestCertificate): string => {
  if (certificate.passed === undefined || certificate.passed === null) {
    return SKIPPED;
  }

  if (
    certificate.bubbleTestType &&
    certificate.bubbleTestType.length > 0 &&
    certificate.testGas &&
    certificate.testGas.length > 0
  ) {
    return certificate.passed ? PASSED : FAILED;
  }

  return "";
};

async function createPrintRequest(
  auth: AuthContext,
  certificate: LeakTestCertificate,
  printerName: string,
  printLocation: string,
): Promise<[PrintRequest, string]> {
  let templatePath = getPathItem(
    await getOption(auth, `Template:${certificate.testType}`),
  );
  console.log(
    `createPrintRequest for ${certificate.testType} templateName=${templatePath.fileName}`,
  );
  const parameters = await getTemplateParameters(
    auth,
    templatePath.folder,
    templatePath.fileName,
    certificate.id.toString(),
  );
  const substr: {
    [key: string]: string;
  } = {};

  parameters.forEach(
    (p, index) => (substr[p.name] = p?.value?.toString() ?? ""),
  );

  const request: PrintRequest = {
    templateName: `${templatePath.folder}\\${templatePath.fileName}`,
    substrings: substr,
    noLabels:
      (parameters.find((p) => p.name === "NoLabels")?.value as number) ?? 1,
    printerName: printerName ?? "",
    location: printLocation ?? "",
  };
  return [request, templatePath.fileName];
}

export function validateTest(
  certificate: LeakTestCertificate,
): string | undefined {
  if (certificate) {
    let error = "";

    if (
      certificate.testType === TestType[TestType.LeakTestInboard] ||
      certificate.testType === TestType[TestType.LeakTestInboard]
    ) {
      if (
        certificate.leakRate > 0 &&
        certificate.exp !== 0 &&
        certificate.acceptableRate > 0 &&
        certificate.acceptableExp !== 0
      ) {
        const actualLeak = certificate.leakRate * Math.pow(10, certificate.exp);
        const acceptableLeak =
          certificate.acceptableRate * Math.pow(10, certificate.acceptableExp);
        //certificate.passed = actualLeak < acceptableLeak;
        certificate.status = actualLeak < acceptableLeak ? "Passed" : "Failed";
      }

      if (certificate.leakRate <= 0) {
        error += "The leak rate is required. ";
      }
      if (certificate.exp === 0) {
        error += "The leak exp is required. ";
      }
    } else if (
      certificate.testType === TestType[TestType.PressureSet] ||
      certificate.testType === TestType[TestType.PressureTest]
    ) {
      //certificate.status = getTestResult(certificate);
      if (
        !certificate.bubbleTestType ||
        certificate.bubbleTestType.length === 0
      ) {
        error += "The bubble test type is required. ";
      }

      if (!certificate.testGas || certificate.testGas.length === 0) {
        error += "The test gas is required. ";
      }
    }

    if (certificate.status === undefined || certificate.status.length === 0) {
      error += "The test result is required. ";
    }

    if (!certificate.station || certificate.station.length === 0) {
      error += "The ToolSet field is required. ";
    }
    console.log(`validate ${JSON.stringify(certificate)} return ${error}`);
    return error;
  }
  return undefined;
}

export async function savePdf(
  auth: AuthContext,
  certificate: LeakTestCertificate | undefined,
): Promise<void> {
  try {
    if (!certificate) {
      ToastUtility.show("No certificate to print", "Error", 2000);
      return;
    }

    const [request, templateName] = await createPrintRequest(
      auth,
      certificate,
      "",
      "",
    );

    const data = await printTemplateAsPdf(auth, request);
    if (data) {
      const href = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", replaceExtension(templateName, "pdf")); //or any other extension
      document.body.appendChild(link);
      link.click();

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    }
  } catch (error) {
    console.error(`Failed to print the certificate ${error}`);
    ToastUtility.show("Failed to print the certificate", "Error", 2000);
  }
}

export async function printCertificate(
  auth: AuthContext,
  certificate: LeakTestCertificate | undefined,
  printerName: string,
  printLocation: string,
): Promise<void> {
  try {
    if (!certificate) {
      ToastUtility.show("No certificate to print", "Error", 2000);
      return;
    }

    const [request] = await createPrintRequest(
      auth,
      certificate,
      printerName,
      printLocation,
    );

    await printTemplate(auth, request);
    ToastUtility.show("The print job sent to the spooler.", "Info", 2000);
  } catch (error) {
    console.error(`Failed to print the certificate ${error}`);
    ToastUtility.show("Failed to print the certificate", "Error", 2000);
  }
}

function populateDefaultsForLeakTestCertificate(
  certificate: LeakTestCertificate,
  lastSavedCertificate: LeakTestCertificate,
) {
  if (certificate.exp === 0) {
    certificate.testType = lastSavedCertificate.testType;
    certificate.testDuration = lastSavedCertificate.testDuration;
    certificate.leakRate = lastSavedCertificate.leakRate;
    certificate.exp = lastSavedCertificate.exp;
    certificate.leakTwo = lastSavedCertificate.leakTwo;
  }
}

function populateDefaultsForPressTestCertificate(
  certificate: LeakTestCertificate,
  lastSavedCertificate: LeakTestCertificate,
) {
  if (certificate.testGas === undefined || certificate.testGas.length === 0) {
    certificate.bubbleTestType = lastSavedCertificate.bubbleTestType;
    certificate.testGas = lastSavedCertificate.testGas;
    certificate.testPressure = lastSavedCertificate.testPressure;
  }
}

function populateDefaultsForPressSetCertificate(
  certificate: LeakTestCertificate,
  lastSavedCertificate: LeakTestCertificate,
) {
  if (certificate.testGas === undefined || certificate.testGas.length === 0) {
    certificate.testGas = lastSavedCertificate.testGas;
    certificate.testPressure = lastSavedCertificate.testPressure;
    certificate.springKit = lastSavedCertificate.springKit;
    certificate.temperature = lastSavedCertificate.temperature;
  }
}

function populateDefaultsForCertificate(
  certificate: LeakTestCertificate,
  lastSavedCertificate: LeakTestCertificate,
) {
  if (certificate.id > 0) {
    return;
  }
  if (
    certificate.testType === TestType[TestType.LeakTestInboard] ||
    certificate.testType === TestType[TestType.LeakTestInboard]
  ) {
    populateDefaultsForLeakTestCertificate(certificate, lastSavedCertificate);
  } else if (certificate.testType === TestType[TestType.PressureTest]) {
    populateDefaultsForPressTestCertificate(certificate, lastSavedCertificate);
  } else if (certificate.testType === TestType[TestType.PressureSet]) {
    populateDefaultsForPressSetCertificate(certificate, lastSavedCertificate);
  }
}

function getLastSavedCertificate(
  certificates: LeakTestCertificate[],
): LeakTestCertificate | undefined {
  const maxId = Math.max(
    ...certificates.filter((c) => c.passed === true).map((c) => c.id),
  );

  if (maxId > 0) {
    return certificates.find((c) => c.id === maxId);
  }

  return undefined;
}

export function updateDefaultValues(
  certificate: LeakTestCertificate,
  certificates: LeakTestCertificate[],
) {
  const lastSavedCertificate = getLastSavedCertificate(certificates);
  if (lastSavedCertificate) {
    populateDefaultsForCertificate(certificate, lastSavedCertificate);
  }
}

export function populateDefaults(
  certificates: LeakTestCertificate[],
): LeakTestCertificate[] {
  const lastSavedCertificate = getLastSavedCertificate(certificates);
  if (lastSavedCertificate) {
    const populatedCertificates = certificates.map((c) => {
      populateDefaultsForCertificate(c, lastSavedCertificate);
      return c;
    });

    return populatedCertificates;
  }

  return certificates;
}
